import classNames from "classnames/bind";

import { type TableConfig, type TableColumnConfig } from "../../../../types/table";
import { getCellSizeProps } from "./utils";

import styles from "./Table.module.scss";

const cx = classNames.bind(styles);

type TableProps<TItem> = {
  config: TableConfig<TItem>;
  data: TItem[];
  className?: string;
};

type HeaderProps<TItem> = {
  config: TableConfig<TItem>;
};

type HeaderCellProps<TItem> = {
  column: TableColumnConfig<TItem>;
};

type BodyProps<TItem> = {
  config: TableConfig<TItem>;
  data: TItem[];
};

type BodyRowProps<TItem> = {
  config: TableConfig<TItem>;
  item: TItem;
};

type BodyCellProps<TItem> = {
  column: TableColumnConfig<TItem>;
  item: TItem;
};

export const Table = <TItem extends object>({ config, data, className }: TableProps<TItem>) => {
  return (
    <div className={className}>
      <Table.Header config={config} />
      <Table.TableBody config={config} data={data} />
    </div>
  );
};

const Header = <TItem extends object>({ config }: HeaderProps<TItem>) => {
  const renderHeaderCell = (column: TableColumnConfig<TItem>, index: number) => {
    if (column.hidden) {
      return null;
    }

    return <Table.HeaderCell key={index} column={column} />;
  };

  return <div className={cx("header", config.headerClassName)}>{config.columns.map(renderHeaderCell)}</div>;
};

const HeaderCell = <TItem extends object>({ column }: HeaderCellProps<TItem>) => {
  const className = cx("headerCell", column.className);

  const headerProps = getCellSizeProps(column);

  return (
    <div className={className} {...headerProps}>
      {column.header}
    </div>
  );
};

const TableBody = <TItem extends object>({ config, data }: BodyProps<TItem>) => {
  const renderItemRow = (item: TItem, index: number) => {
    return <Table.BodyRow key={index} config={config} item={item} />;
  };

  return <div>{data.map(renderItemRow)}</div>;
};

const BodyRow = <TItem extends object>({ config, item }: BodyRowProps<TItem>) => {
  const renderBodyCell = (column: TableColumnConfig<TItem>, index: number) => {
    if (column.hidden) {
      return null;
    }

    return <Table.BodyCell key={index} column={column} item={item} />;
  };

  const rowClassName = config.getRowClassName?.(item);

  const className = cx("row", rowClassName, config.rowClassName);

  const clickable = config.onRowClick || config.onRowDoubleClick;

  const onClick = () => {
    config.onRowClick?.(item);
  };

  return (
    <div role={clickable ? "button" : "listitem"} onClick={onClick} onKeyDown={onClick} className={className}>
      {config.columns.map(renderBodyCell)}
    </div>
  );
};

const BodyCell = <TItem extends object>({ column, item }: BodyCellProps<TItem>) => {
  const className = cx("bodyCell", column.className);
  const renderColumnBody = () => {
    const renderedBody = column.render(item);

    if (typeof renderedBody === "object") {
      return renderedBody;
    }

    return renderedBody;
  };

  return <div className={className}>{renderColumnBody()}</div>;
};

Table.Header = Header;
Table.HeaderCell = HeaderCell;
Table.TableBody = TableBody;
Table.BodyRow = BodyRow;
Table.BodyCell = BodyCell;
